import React, { useState, useEffect, useRef, useCallback } from "react";
import { uploadAudioForDiagnosis, diagnoseAI } from "api/diagnose";
import { createMatchingRequest } from "firebase_/firebase_function/matching";
import { useUser } from "util/context/user_context";

const BRAND = "#2E4A24";
const BG = "#BACEE0";
const YELLOW = "#F7E600";

/* ──────────────────────────────────────────
   메인 Chatbot 컴포넌트 — 우측 하단 고정 토글
────────────────────────────────────────── */
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 10,
      }}
    >
      {isOpen && <ChatWindow onClose={() => setIsOpen(false)} />}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {/* 기능 안내 라벨 — 버튼이 무엇인지 한눈에 알 수 있도록 */}
        {!isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            aria-label="AI 고장 진단 열기"
            style={{
              height: 40,
              padding: "0 16px",
              borderRadius: 9999,
              border: "none",
              background: "#fff",
              color: BRAND,
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(0,0,0,.15)",
              whiteSpace: "nowrap",
              fontFamily: "inherit",
            }}
          >
            🔧 AI 고장 진단
          </button>
        )}
        <ChatButton onClick={() => setIsOpen((p) => !p)} isOpen={isOpen} />
      </div>
    </div>
  );
};

/* ──────────────────────────────────────────
   ChatWindow — STT + 텍스트 입력 + 진단 결과 + 수리요청 등록
────────────────────────────────────────── */
const ChatWindow = ({ onClose }) => {
  const { userData } = useUser();
  const scrollRef = useRef();

  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "bot",
      text:
        "안녕하세요! 무엇을 도와드릴까요?\n음성 버튼을 눌러 말씀하시거나, 텍스트로 증상을 입력해주세요.",
    },
  ]);

  // 0: 입력 대기, 3: 녹음완료, 4: 진단중, 5: 진단결과, 6: 진단실패
  // 1: 사진 단계, 2: 사진확인, 7: 등록중
  const [chatState, setChatState] = useState(0);

  // 녹음
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSec, setRecordingSec] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // 텍스트
  const [inputText, setInputText] = useState("");

  // 진단 결과 보관 (수리요청 시 사용)
  const [finalContent, setFinalContent] = useState("");

  // 사진
  const [images, setImages] = useState([]);
  const imageInputRef = useRef(null);

  // 오디오 재생
  const [playingId, setPlayingId] = useState(null);
  const audioElRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, chatState]);

  const addBot = useCallback((text) => {
    setMessages((p) => [
      ...p,
      { id: Date.now() + Math.random(), type: "bot", text },
    ]);
  }, []);

  const addUserVoice = useCallback((url, sec) => {
    setMessages((p) => [
      ...p,
      {
        id: Date.now() + Math.random(),
        type: "user_voice",
        audioUrl: url,
        duration: sec,
      },
    ]);
  }, []);

  const addUserImages = useCallback((files) => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setMessages((p) => [
      ...p,
      { id: Date.now() + Math.random(), type: "user_image", imageUrls: urls },
    ]);
  }, []);

  const fmt = (s) => {
    const m = String(Math.floor(s / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    return `${m}:${sec}`;
  };

  /* 녹음 */
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm;codecs=opus" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setIsRecording(true);
      setRecordingSec(0);
      timerRef.current = setInterval(() => {
        setRecordingSec((p) => {
          if (p >= 179) {
            stopRecording();
            return p;
          }
          return p + 1;
        });
      }, 1000);
    } catch {
      addBot("마이크 권한이 필요합니다. 브라우저 설정에서 마이크를 허용해주세요.");
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  useEffect(() => {
    if (audioUrl && !isRecording && chatState === 0) {
      if (recordingSec < 1) {
        addBot("녹음 시간이 너무 짧습니다. 다시 녹음해주세요.");
        return;
      }
      addUserVoice(audioUrl, recordingSec);
      addBot("음성이 녹음되었습니다. 다시 녹음하시거나 AI 진단을 진행해주세요.");
      setChatState(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl, isRecording]);

  const toggleRecording = () => {
    if (isRecording) stopRecording();
    else startRecording();
  };

  /* 음성 재생 */
  const togglePlay = (id, url) => {
    if (playingId === id) {
      audioElRef.current?.pause();
      setPlayingId(null);
    } else {
      if (audioElRef.current) audioElRef.current.pause();
      const a = new Audio(url);
      a.onended = () => setPlayingId(null);
      a.play();
      audioElRef.current = a;
      setPlayingId(id);
    }
  };

  /* 텍스트 진단 */
  const handleTextSubmit = async () => {
    const text = inputText.trim();
    if (!text) return;
    setMessages((p) => [...p, { id: Date.now(), type: "user_text", text }]);
    setInputText("");
    setFinalContent(text);
    setChatState(4);
    addBot("내용을 분석하고 있습니다. 잠시만 기다려주세요...");

    try {
      const result = await diagnoseAI(text);
      handleDiagnosisResult(result, text);
    } catch {
      addBot("서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.");
      setChatState(6);
    }
  };

  /* 음성 진단 */
  const requestVoiceDiagnosis = async () => {
    if (!audioBlob) return;
    setChatState(4);
    addBot("내용을 분석하고 있습니다. 잠시만 기다려주세요...");

    try {
      const result = await uploadAudioForDiagnosis(audioBlob);
      if (!result || result.success !== true) {
        addBot("음성을 인식하지 못했습니다. 다시 녹음해주세요.");
        setChatState(3);
        return;
      }
      const transcript = result.transcribed_text;
      if (!transcript) {
        addBot("음성을 인식하지 못했습니다. 다시 녹음해주세요.");
        setChatState(3);
        return;
      }
      addBot(`음성 인식 결과: "${transcript}"`);
      setFinalContent(transcript);
      handleDiagnosisResult(result, transcript);
    } catch {
      addBot("서버 연결에 실패했습니다. 잠시 후 다시 시도해주세요.");
      setChatState(3);
    }
  };

  /* 진단 결과 처리 */
  const handleDiagnosisResult = (result) => {
    const diagnosis = result.diagnosis;
    if (!diagnosis) {
      addBot("진단 결과를 받지 못했습니다. 다시 시도하시거나 수리 요청을 등록해주세요.");
      setChatState(6);
      return;
    }
    if (diagnosis.is_relevant !== true) {
      const reason =
        diagnosis.reject_reason || "장비 고장과 관련 없는 내용입니다.";
      addBot(reason);
      addBot("증상을 더 자세히 입력하시거나 수리 요청을 등록해주세요.");
      setChatState(6);
      return;
    }
    setMessages((prev) => [
      ...prev,
      { id: Date.now() + Math.random(), type: "bot_diagnosis", diagnosis },
    ]);
    if (!diagnosis.requires_expert) {
      addBot("위 방법으로 직접 해결이 가능할 것 같습니다. 해결되셨나요?");
    }
    setChatState(5);
  };

  /* 사진 */
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > 10) {
      addBot("사진은 최대 10장까지 등록 가능합니다.");
      return;
    }
    const newImages = [...images, ...files];
    setImages(newImages);
    addUserImages(files);
    addBot("해당 내용을 바탕으로 요청을 진행하시겠습니까, 아니면 추가 사진을 등록하시겠습니까?");
    setChatState(2);
    e.target.value = "";
  };

  const removeImage = (idx) => {
    setImages((p) => {
      const next = p.filter((_, i) => i !== idx);
      if (next.length === 0) setChatState(1);
      return next;
    });
  };

  /* 수리요청 등록 */
  const submitRequest = async () => {
    if (!userData?.id) {
      addBot("로그인이 필요합니다.");
      return;
    }
    setChatState(7);
    addBot("요청을 등록하고 있습니다...");

    try {
      const result = await createMatchingRequest(
        userData,
        [0, 0, 0],
        ["", "", ""],
        finalContent,
        images,
      );
      if (result) {
        addBot("요청이 정상적으로 등록되었습니다!");
        setChatState(0);
        setFinalContent("");
        setAudioBlob(null);
        setAudioUrl(null);
        setImages([]);
      } else {
        addBot("요청 등록에 실패했습니다. 다시 시도해주세요.");
        setChatState(1);
      }
    } catch {
      addBot("요청 등록에 실패했습니다. 다시 시도해주세요.");
      setChatState(1);
    }
  };

  const reRecord = () => setChatState(0);
  const goRepairRequest = () => {
    addBot("사진을 올려주시거나, 바로 수리 요청 등록을 진행해주세요.");
    setChatState(1);
  };

  const handleKeyDown = (e) => {
    if (e.nativeEvent.isComposing) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleTextSubmit();
    }
  };

  return (
    <div
      style={{
        width: 360,
        height: 580,
        backgroundColor: BG,
        borderRadius: 16,
        boxShadow: "0 5px 25px rgba(0,0,0,0.25)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        marginBottom: 10,
        animation: "ttFadeIn .3s ease-out",
        fontFamily:
          'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
      }}
    >
      <style>{`
        @keyframes ttFadeIn { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes ttBounce { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-5px)} }
        @keyframes ttPulse { 0%,100%{opacity:1} 50%{opacity:.5} }
      `}</style>

      <div
        style={{
          padding: "14px 16px",
          backgroundColor: "rgba(255,255,255,.45)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid rgba(0,0,0,.05)",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 16, color: "#1F2937" }}>
          뚝딱이 챗봇
        </span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 18,
            color: "#666",
          }}
        >
          ✕
        </button>
      </div>

      <div
        ref={scrollRef}
        style={{
          flex: 1,
          padding: 14,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {messages.map((msg) => {
          if (msg.type === "bot") return <BotBubble key={msg.id} text={msg.text} />;
          if (msg.type === "bot_diagnosis")
            return <DiagnosisBubble key={msg.id} d={msg.diagnosis} />;
          if (msg.type === "user_text")
            return <UserBubble key={msg.id} text={msg.text} />;
          if (msg.type === "user_voice")
            return (
              <VoiceBubble
                key={msg.id}
                id={msg.id}
                url={msg.audioUrl}
                sec={msg.duration}
                playing={playingId === msg.id}
                onToggle={() => togglePlay(msg.id, msg.audioUrl)}
                fmt={fmt}
              />
            );
          if (msg.type === "user_image")
            return <ImageBubble key={msg.id} urls={msg.imageUrls} />;
          return null;
        })}

        {chatState === 4 && <LoadingBubble />}
      </div>

      <div
        style={{
          backgroundColor: "#fff",
          borderTop: "1px solid #e5e7eb",
          padding: "10px 12px",
        }}
      >
        {isRecording && (
          <div
            style={{
              textAlign: "center",
              marginBottom: 8,
              fontSize: 15,
              fontWeight: 600,
              color: "#EF4444",
              animation: "ttPulse 1s infinite",
            }}
          >
            녹음 중 {fmt(recordingSec)}
          </div>
        )}

        {chatState === 0 && !isRecording && (
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="증상을 입력하세요 (예: 트랙터 시동 안 걸려)"
              style={{
                flex: 1,
                padding: "10px 12px",
                border: "1px solid #E5E7EB",
                borderRadius: 8,
                fontSize: 14,
                outline: "none",
              }}
            />
            {inputText.trim() ? (
              <Btn color={BRAND} textColor="#fff" onClick={handleTextSubmit}>
                진단
              </Btn>
            ) : (
              <Btn color="#3B82F6" textColor="#fff" onClick={toggleRecording}>
                <MicIcon />
              </Btn>
            )}
          </div>
        )}

        {chatState === 0 && isRecording && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              onClick={toggleRecording}
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "#EF4444",
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <StopIcon />
            </button>
          </div>
        )}

        {chatState === 3 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn color="#E5E7EB" textColor="#1F2937" onClick={reRecord} full>
                다시 녹음
              </Btn>
              <Btn color="#059669" textColor="#fff" onClick={requestVoiceDiagnosis} full>
                AI 진단
              </Btn>
            </div>
            <Btn color={YELLOW} textColor="#1F2937" onClick={goRepairRequest} full>
              진단 없이 수리 요청
            </Btn>
          </div>
        )}

        {chatState === 4 && (
          <div style={{ textAlign: "center", padding: 8, color: "#6B7280", fontSize: 14 }}>
            분석 중...
          </div>
        )}

        {chatState === 5 && (
          <div style={{ display: "flex", gap: 8 }}>
            <Btn
              color="#059669"
              textColor="#fff"
              onClick={() => {
                addBot("다행입니다! 문제가 해결되셨군요. 추가 도움이 필요하시면 다시 이용해주세요.");
                setChatState(0);
                setFinalContent("");
                setAudioBlob(null);
                setAudioUrl(null);
              }}
              full
            >
              해결됨
            </Btn>
            <Btn color={YELLOW} textColor="#1F2937" onClick={goRepairRequest} full>
              수리 요청
            </Btn>
          </div>
        )}

        {chatState === 6 && (
          <div style={{ display: "flex", gap: 8 }}>
            <Btn color="#E5E7EB" textColor="#1F2937" onClick={reRecord} full>
              다시 시도
            </Btn>
            <Btn color={YELLOW} textColor="#1F2937" onClick={goRepairRequest} full>
              수리 요청
            </Btn>
          </div>
        )}

        {chatState === 1 && (
          <div style={{ display: "flex", gap: 8 }}>
            <Btn color="#E5E7EB" textColor="#1F2937" onClick={() => imageInputRef.current?.click()} full>
              사진 올리기
            </Btn>
            <Btn color={YELLOW} textColor="#1F2937" onClick={submitRequest} full>
              바로 등록
            </Btn>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={handleImageChange}
            />
          </div>
        )}

        {chatState === 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }}>
              {images.map((file, idx) => (
                <div key={idx} style={{ position: "relative", flexShrink: 0 }}>
                  <img
                    src={URL.createObjectURL(file)}
                    alt=""
                    style={{ width: 60, height: 60, borderRadius: 8, objectFit: "cover" }}
                  />
                  <button
                    onClick={() => removeImage(idx)}
                    style={{
                      position: "absolute",
                      top: -4,
                      right: -4,
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      backgroundColor: "#EF4444",
                      border: "none",
                      color: "#fff",
                      fontSize: 11,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn color="#E5E7EB" textColor="#1F2937" onClick={() => imageInputRef.current?.click()} full>
                추가 사진
              </Btn>
              <Btn color={YELLOW} textColor="#1F2937" onClick={submitRequest} full>
                요청 등록
              </Btn>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={handleImageChange}
              />
            </div>
          </div>
        )}

        {chatState === 7 && (
          <div style={{ textAlign: "center", padding: 8, color: "#6B7280", fontSize: 14 }}>
            등록 중...
          </div>
        )}
      </div>
    </div>
  );
};

/* ── 진단 결과 카드 ── */
const DiagnosisBubble = ({ d }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
    <BotAvatar />
    <div
      style={{
        maxWidth: "85%",
        padding: 14,
        borderRadius: 12,
        borderTopLeftRadius: 0,
        backgroundColor: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,.1)",
        fontSize: 13,
        lineHeight: 1.6,
        color: "#1F2937",
      }}
    >
      {d.symptom_summary && <Section title="증상">{d.symptom_summary}</Section>}
      {d.category && (
        <Section title="분류">
          <span
            style={{
              display: "inline-block",
              padding: "2px 10px",
              borderRadius: 12,
              backgroundColor: "#ECFDF5",
              color: "#059669",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {d.category}
          </span>
        </Section>
      )}
      {d.possible_causes?.length > 0 && (
        <Section title="예상 원인">
          <ol style={{ margin: 0, paddingLeft: 18 }}>
            {d.possible_causes.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ol>
        </Section>
      )}
      {d.self_repair_steps?.length > 0 && (
        <Section title="자가 수리 방법">
          {d.self_repair_steps.map((s, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              <span style={{ fontWeight: 700, color: BRAND }}>{s.step}.</span>{" "}
              {s.action}
              {s.difficulty && (
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 11,
                    padding: "1px 6px",
                    borderRadius: 4,
                    backgroundColor: "#FEF3C7",
                    color: "#92400E",
                  }}
                >
                  {s.difficulty}
                </span>
              )}
            </div>
          ))}
        </Section>
      )}
      {d.safety_warning && (
        <div
          style={{
            marginTop: 10,
            padding: 10,
            borderRadius: 8,
            backgroundColor: "#FEF2F2",
            border: "1px solid #FECACA",
            fontSize: 13,
            color: "#991B1B",
          }}
        >
          <span style={{ fontWeight: 700 }}>⚠ 주의:</span> {d.safety_warning}
        </div>
      )}
      {d.requires_expert && d.expert_reason && (
        <div
          style={{
            marginTop: 10,
            padding: 10,
            borderRadius: 8,
            backgroundColor: "#EFF6FF",
            border: "1px solid #BFDBFE",
            fontSize: 13,
            color: "#1E40AF",
          }}
        >
          <span style={{ fontWeight: 700 }}>전문가 권장:</span> {d.expert_reason}
        </div>
      )}
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", marginBottom: 4 }}>
      [ {title} ]
    </div>
    <div>{children}</div>
  </div>
);

const BotAvatar = () => (
  <div
    aria-label="뚝딱이 챗봇"
    style={{
      width: 34,
      height: 34,
      borderRadius: 10,
      background: "linear-gradient(135deg, #047857 0%, #2E4A24 100%)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      boxShadow: "0 2px 4px rgba(46,74,36,.18)",
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* 렌치 + 작은 스파크 아이콘 — 뚝딱이 수리 브랜드와 어울리는 화이트 톤 */}
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </svg>
    <span
      style={{
        position: "absolute",
        top: 4,
        right: 4,
        width: 6,
        height: 6,
        borderRadius: 9999,
        background: "#FACC15",
        boxShadow: "0 0 0 1.5px #fff",
      }}
    />
  </div>
);

const BotBubble = ({ text }) => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
    <BotAvatar />
    <div
      style={{
        maxWidth: "80%",
        padding: "10px 14px",
        borderRadius: 12,
        borderTopLeftRadius: 0,
        backgroundColor: "#fff",
        color: "#1F2937",
        fontSize: 14,
        lineHeight: 1.5,
        boxShadow: "0 1px 2px rgba(0,0,0,.08)",
        whiteSpace: "pre-line",
      }}
    >
      {text}
    </div>
  </div>
);

const UserBubble = ({ text }) => (
  <div style={{ display: "flex", justifyContent: "flex-end" }}>
    <div
      style={{
        maxWidth: "75%",
        padding: "10px 14px",
        borderRadius: 12,
        borderTopRightRadius: 0,
        backgroundColor: BRAND,
        color: "#fff",
        fontSize: 14,
        lineHeight: 1.4,
        wordBreak: "break-word",
      }}
    >
      {text}
    </div>
  </div>
);

const VoiceBubble = ({ id, url, sec, playing, onToggle, fmt }) => (
  <div style={{ display: "flex", justifyContent: "flex-end" }}>
    <button
      onClick={onToggle}
      style={{
        padding: "10px 16px",
        borderRadius: 12,
        borderTopRightRadius: 0,
        backgroundColor: YELLOW,
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      {playing ? <StopSmallIcon /> : <PlayIcon />}
      <span style={{ fontSize: 14, fontWeight: 600, color: "#1F2937" }}>
        {playing ? "재생 중..." : `음성 메시지 ${fmt(sec)}`}
      </span>
    </button>
  </div>
);

const ImageBubble = ({ urls }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", gap: 4, flexWrap: "wrap" }}>
    {urls.map((u, i) => (
      <img
        key={i}
        src={u}
        alt=""
        style={{ width: 80, height: 80, borderRadius: 8, objectFit: "cover" }}
      />
    ))}
  </div>
);

const LoadingBubble = () => (
  <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
    <BotAvatar />
    <div
      style={{
        padding: "12px 16px",
        borderRadius: 12,
        borderTopLeftRadius: 0,
        backgroundColor: "#fff",
        display: "flex",
        gap: 4,
      }}
    >
      {[0, 0.2, 0.4].map((d, i) => (
        <div
          key={i}
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            backgroundColor: "#9CA3AF",
            animation: `ttBounce .6s infinite ease-in-out ${d}s`,
          }}
        />
      ))}
    </div>
  </div>
);

const Btn = ({ color, textColor, onClick, full, children }) => (
  <button
    onClick={onClick}
    style={{
      padding: "10px 16px",
      borderRadius: 8,
      border: "none",
      backgroundColor: color,
      color: textColor,
      fontSize: 14,
      fontWeight: 600,
      cursor: "pointer",
      flex: full ? 1 : undefined,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 4,
      transition: "opacity .15s",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </button>
);

const MicIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" />
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" />
  </svg>
);
const StopIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);
const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#1F2937">
    <path d="M8 5v14l11-7z" />
  </svg>
);
const StopSmallIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#1F2937">
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </svg>
);

const ChatButton = ({ onClick, isOpen }) => (
  <button
    onClick={onClick}
    aria-label="챗봇 열기"
    style={{
      width: 60,
      height: 60,
      borderRadius: "50%",
      backgroundColor: BRAND,
      boxShadow: "0 4px 8px rgba(0,0,0,.2)",
      border: "none",
      cursor: "pointer",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      transition: "transform .2s",
      transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
    }}
  >
    {isOpen ? (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#fff"
        strokeWidth="2.5"
        strokeLinecap="round"
      >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    ) : (
      <svg width="30" height="30" viewBox="0 0 24 24" fill="#fff">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
      </svg>
    )}
  </button>
);

export default Chatbot;
